import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useSession } from '../../state/useSession';
import { takeUpEmailLink, useBuildRun } from '../../state/buildRun';
import { readIntent } from '../../state/buildIntent';
import { openEmailLink } from '../../api/emailLink';
import { readyLinkRefusal, requestReadyLink } from '../../api/readyLink';
import { socialProviders } from '../../api/oauth';
import { inAppBrowser } from '../../analytics/device';
import { ApiError } from '../../api/client';
import { useDraft } from '../../state/draft';
import { stashDraft, type StashResult } from '../../state/draftStash';
import { notKept } from './Plan';
import { creditBalance, startCheckout } from '../../api/billing';
import { freeWeekEnd, offerFor, rememberCheckoutOffer, reportInitiateCheckout, type Offer } from '../../state/trial';
import { trialEndFromNow, type PlanId } from '../../state/plans';
import { leaveFor } from '../../platform/leave';
import { funnelStep, useFunnelStep } from '../../analytics/funnel';
import { secondsBucket } from '../../analytics/visits';
import type { AppPlan } from '@/webapp/plan/planTypes';
import SignUp from '../auth/SignUp';
import Paywall from './Paywall';
import ExitQuestion, { exitQuestionAsked } from './ExitQuestion';
import { Button, Screen, TextField } from '../../ui';
import { useWhat } from '../../state/what';
import { TYPES } from '../../journey/questions';
import { emailNoun } from '../../journey/demos';
import { earlyEmail, forgetEarlyEmail } from '../../state/earlyEmail';

/** How long their own site is theirs to look at before the offer slides up (owner, 2026-10-08). */
export const PREVIEW_BEFORE_OFFER_MS = 15_000;

/**
 * /new/building — the owner's flow (2026-10-08): the site renders while the account is made, is shown
 * once it is, and the offer comes over it a little later.
 *
 *  1. Building: their app's name and the step it is on — and, signed out, the account box (Apple or
 *     email). The build runs under the browser's id meanwhile (state/buildRun.ts).
 *  2. Signed in and built: their own site, full screen, framed and unpublished.
 *  3. After PREVIEW_BEFORE_OFFER_MS: the trial offer slides up over the dimmed site and stays. Closed,
 *     it leaves a bar that opens it again; the site stays dimmed. The card is the way on.
 *
 * Subscribers never come here: Build builds and opens the editor, as before.
 */
export default function Building() {
  const navigate = useNavigate();
  const { user, isAuthenticated, isInitializing } = useSession();
  const status = useBuildRun((s) => s.status);
  const phase = useBuildRun((s) => s.phase);
  const releasing = useBuildRun((s) => s.releasing);
  const heldApp = useBuildRun((s) => s.heldApp);
  const error = useBuildRun((s) => s.error);
  const outOfCredits = useBuildRun((s) => s.outOfCredits);
  const draft = useDraft();
  const appName = heldApp?.name || draft.plan?.appName || draft.appName || 'your app';
  const [nothing, setNothing] = useState(false);
  /** V7: "I'll wait here" chosen — today's sign-up card instead of the email. */
  const [waitHere, setWaitHere] = useState(false);
  /** V7: the address "Email me when it's ready" went to, for this build (kept for a reload). */
  const [sentTo, setSentTo] = useState<string | null>(() => readySentFor(readIntent()?.requestKey));

  /*
   * The address given before the build ("Where should we send it?", 2026-11-01), sent by itself once the
   * build is under way. The build's record may reach the server a moment after this screen does, so a
   * "no build yet" is tried again a few times; any other refusal leaves the card to ask, as before.
   */
  const early = useRef(earlyEmail());
  const earlyTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const mounted = useRef(true);
  useEffect(() => () => {
    mounted.current = false;
    clearTimeout(earlyTimer.current);
  }, []);
  useEffect(() => {
    if (isInitializing || isAuthenticated || sentTo || !early.current || (status !== 'running' && !heldApp)) return;
    // Once: the address is taken off the record now, so no later render starts a second send.
    const address = early.current;
    early.current = null;
    forgetEarlyEmail();
    let tries = 0;
    const attempt = async (): Promise<void> => {
      const result = await sendReadyLink(address, appName, true);
      if (!mounted.current) return;
      if (result.ok) setSentTo(address);
      else if (result.code === 'no_build' && ++tries < 5) earlyTimer.current = setTimeout(() => void attempt(), 3000);
    };
    earlyTimer.current = setTimeout(() => void attempt(), 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializing, isAuthenticated, sentTo, status, heldApp]);

  /*
   * Arrived by an "email me my app link" email (?link=): probably not the browser the build began
   * in. The link is traded for the build's choices, this browser takes them up, and the sign-up
   * below — the address already in it — collects the build here.
   */
  const [params, setParams] = useSearchParams();
  const [link, setLink] = useState<'none' | 'opening' | 'opened' | 'gone'>(() => (params.get('link') ? 'opening' : 'none'));
  const [linkEmail, setLinkEmail] = useState('');
  useEffect(() => {
    const token = params.get('link');
    if (!token) return;
    let live = true;
    openEmailLink(token).then(
      (opened) => {
        if (!live) return;
        takeUpEmailLink(opened);
        setLinkEmail(opened.email);
        setLink('opened');
        funnelStep('email_link_opened', { code: opened.state });
      },
      (e: unknown) => {
        if (!live) return;
        setLink('gone');
        funnelStep('email_link_opened', { code: e instanceof ApiError && e.code === 'bad_link' ? 'bad_link' : 'gone' });
      },
    ).finally(() => {
      // The token is not left in the address bar, the history or a shared screenshot.
      if (live) setParams((p) => { p.delete('link'); return p; }, { replace: true });
    });
    return () => {
      live = false;
    };
    // Once, for the link the page was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * Signed in (just now, or after a reload — Apple's return is one): ask again for the build on
   * record under its key. A build begun signed out is claimed by this account; a held one comes back
   * with its preview; a running one is waited for.
   */
  /*
   * A build carried here from an app's own browser by "Open in Safari/Chrome" (platform/openInBrowser.ts,
   * 2026-10-24): its anonymous id and key, taken up as an emailed link's are, then out of the address
   * bar. This browser then offers the email and sign-up for it, and collects it at sign-in.
   */
  useEffect(() => {
    const anonId = params.get('ba');
    const requestKey = params.get('bk');
    if (!anonId || !requestKey) return;
    if (!readIntent()?.requestKey && /^[A-Za-z0-9-]{8,64}$/.test(anonId) && /^[A-Za-z0-9_-]{8,128}$/.test(requestKey)) {
      takeUpEmailLink({
        anonId,
        requestKey,
        goal: params.get('idea') ?? '',
        appName: params.get('bn') ?? '',
        plan: null,
        email: '',
        state: 'running',
        createdAt: new Date().toISOString(),
      });
      funnelStep('build_carried');
    }
    setParams((p) => { p.delete('ba'); p.delete('bk'); p.delete('bn'); p.delete('idea'); return p; }, { replace: true });
    // Once, for the address the page was opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const collected = useRef(false);
  useEffect(() => {
    if (isInitializing || !isAuthenticated || !user || collected.current || link === 'opening') return;
    /*
     * Only from rest: a build that is running is waited for, and one that has just ENDED here — a
     * refusal for credits, a failure — is not asked for again. It was, the moment the refusal landed,
     * so one press of Build sent two requests and drew two refusals (2026-10-09).
     */
    if (status !== 'idle' || heldApp?.previewUrl) return;
    collected.current = true;
    void useBuildRun.getState().collect(user.id).then((found) => {
      if (!found) setNothing(true);
    });
  }, [isInitializing, isAuthenticated, user, status, heldApp, link]);

  /*
   * Back, caught once (owner, 2026-10-25). Three of four ad visitors who started a build stepped back
   * off this screen within seconds and left; their apps finished on the server and nobody ever saw
   * them. While a signed-out build runs or waits, one extra history entry sits on top of this screen:
   * the first Back lands on it and opens "Your app is still being built" with the email, instead of
   * walking back through the plan. "Leave anyway" goes back for real.
   */
  const [leaving, setLeaving] = useState(false);
  const guarded = useRef(false);
  const catchBack = !isInitializing && !isAuthenticated && (status === 'running' || !!heldApp) && !sentTo && link === 'none';
  useEffect(() => {
    if (!catchBack) return;
    if (!guarded.current) {
      window.history.pushState(window.history.state, '', window.location.href);
      guarded.current = true;
    }
    const onPop = (): void => {
      if (!guarded.current) return;
      guarded.current = false;
      setLeaving(true);
      funnelStep('building_back_caught');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [catchBack]);
  const keepWatching = (): void => {
    setLeaving(false);
    if (!guarded.current) {
      window.history.pushState(window.history.state, '', window.location.href);
      guarded.current = true;
    }
  };
  const leaveAnyway = (): void => {
    setLeaving(false);
    funnelStep('building_left_anyway', {}, { now: true });
    window.history.back();
  };

  // A build that landed outright (an account that could pay for it) opens the editor by itself
  // (state/buildRun.ts navigates there), so this screen has nothing to add for it.

  /*
   * The server would hold no build today (one a day per account): nothing to show, so the offer
   * itself — the same panel, the same checkout, and the build follows the card.
   */
  if (isAuthenticated && status === 'error' && outOfCredits) {
    return <SiteThenOffer appName={appName} preview={null} plan={draft.plan} ownerId={user?.id ?? ''} />;
  }

  const showSite = isAuthenticated && !!heldApp?.previewUrl;
  if (showSite) return <SiteThenOffer appName={appName} preview={heldApp!.previewUrl!} plan={draft.plan} ownerId={user?.id ?? ''} />;

  if (link === 'opening') {
    return (
      <Screen>
        <p className="type-body building-step" role="status">Opening your app…</p>
      </Screen>
    );
  }

  if (link === 'gone') {
    return (
      <Screen title="That app is no longer kept">
        <p className="type-body">We keep an app for 3 days, or until an account collects it. Describe it again and Saddle builds it afresh.</p>
        <Button title="Start from your idea" onPress={() => navigate('/new/idea')} />
      </Screen>
    );
  }

  if (nothing && status !== 'running') {
    return (
      <Screen title="Nothing is building">
        <p className="type-body">There’s no app being built in this browser right now.</p>
        <Button title="Start from your idea" onPress={() => navigate('/new/idea')} />
      </Screen>
    );
  }

  /*
   * V6 polish (2026-10-14): the RELEASE of an app the server already holds (state/buildRun.ts
   * `releasing`: the replay after sign-in, the free-week replay), or a build already at its publish,
   * is not a build. One calm line: no clock restarting at 0:06 "usually about 4 minutes", no stage
   * list back at "Reading your plan" over an app that exists.
   */
  if (isAuthenticated && status === 'running' && (releasing || phase === 'publishing')) {
    return (
      <Screen>
        <div className="building building-going-live" role="status" aria-live="polite">
          <span className="building-going-live-spinner" aria-hidden="true" />
          <p className="type-heading building-going-live-line">Your {appName} is built.</p>{' '}
          <p className="type-body building-going-live-sub">Putting it live…</p>
        </div>
      </Screen>
    );
  }

  const built = !!heldApp; // held, signed out: ready, waiting for the account
  const included = draft.plan?.features.filter((f) => f.included) ?? [];
  /*
   * Signed out and building: the account is asked for on this same screen, and "Continue with email"
   * must be on a 390x844 screen without scrolling (V6 polish, 2026-10-14). So the page says "Your …
   * is being built" once (the card does not say it again), the stage is one line, and what is going
   * in folds behind "What’s going in".
   */
  const askingAccount = !isInitializing && !isAuthenticated && status === 'running' && !built;
  const features = included.length > 0 && (
    <ul className="building-features" aria-label="What’s going in">
      {included.slice(0, 6).map((f) => <li key={f.label}>{f.label}</li>)}
    </ul>
  );
  /* Built with the plan's own features and look (handoff §2); either can be changed on the plan. */
  const change = draft.plan && (
    <p className="type-caption building-change">
      <button type="button" className="link-button" onClick={() => navigate('/new/plan')}>Change the features or look</button>
    </p>
  );
  const signedOut = !isInitializing && !isAuthenticated;
  /*
   * V7 (V7-A/04-building-email): signed out, the card is email-first — "Email me when it's ready" —
   * with Apple beside it and "I'll wait here" for today's sign-up. Not for somebody who came by V6's
   * emailed link (they are here to sign up), and not once they chose to wait.
   */
  const emailFirst = signedOut && link === 'none' && !waitHere && (status === 'running' || built);
  const goingIn = (features || change) && (
    <details className="building-going-in">
      <summary className="type-caption">What’s going in</summary>
      {features}
      {change}
    </details>
  );
  if (askingAccount || emailFirst) {
    return (
      <Screen>
        <div className="building building-compact building-v7">
          {status === 'running' ? (
            <BuildHeader startedAt={buildStartedAt()} title={`Building your ${appName}`} />
          ) : (
            <h1 className="type-heading building-name">Your {appName} is built</h1>
          )}
        </div>
        {sentTo ? (
          <>
            <ReadySent email={sentTo} built={built} />
            {status === 'running' && <StageList startedAt={buildStartedAt()} />}
          </>
        ) : emailFirst ? (
          <>
            <ReadyByEmail appName={appName} onSent={(to) => setSentTo(to)} onWait={() => setWaitHere(true)} />
            {goingIn}
          </>
        ) : (
          <>
            <SignUp
              card={{
                from: '/new/building',
                // The page above already says it is being built, so the card does not.
                headline: null,
                sub: 'Create your account while it builds. You’ll see it the moment it’s ready.',
                ...(linkEmail ? { email: linkEmail } : {}),
              }}
            />
            {goingIn}
          </>
        )}
        {leaving && (
          <div className="leave-sheet-backdrop">
            <section className="card clay-card leave-sheet" role="dialog" aria-modal="true" aria-labelledby="leave-sheet-title">
              <h2 id="leave-sheet-title" className="type-title">{status === 'running' ? `Your ${appName === 'your app' ? 'app' : appName} is still being built` : `Your ${appName === 'your app' ? 'app' : appName} is built`}</h2>
              <p className="type-body leave-sheet-lead">
                {status === 'running' ? 'It’s ready in about 4 minutes.' : 'It’s ready now.'} Leave your email and we’ll send you the link, so it isn’t lost.
              </p>
              <ReadyByEmail appName={appName} onSent={(to) => { setSentTo(to); setLeaving(false); }} onWait={keepWatching} />
              <button type="button" className="link-button leave-sheet-leave" onClick={leaveAnyway}>Leave anyway</button>
            </section>
          </div>
        )}
      </Screen>
    );
  }
  return (
    <Screen>
      <div className="building">
        <h1 className="type-title building-name">{appName}</h1>
        {(
          <p className="type-body building-step" role="status" aria-live="polite">
            {status === 'error'
              ? error ?? 'That build did not finish.'
              : built
                ? 'Your app is built and ready to see.'
                : status === 'running'
                  ? isAuthenticated
                    ? 'You’re in. Your app is being built, and it opens here by itself the moment it’s ready.'
                    : 'Your app is being built right now.'
                  : 'Getting your app ready…'}
          </p>
        )}
        {status === 'running' && <BuildProgress startedAt={buildStartedAt()} />}
        {/* What is going in — the plan they just said yes to — so the wait is never a blank page. */}
        {status === 'running' && (
          <>
            {features}
            {change}
          </>
        )}
      </div>
      {signedOut && (
        <SignUp
          card={{
            from: '/new/building',
            // Building: the page above already says what it is, so the card does not.
            headline: built ? `Create your account to see your ${appName}` : null,
            sub: built
              ? 'It’s ready. Your account keeps it, and you see it the moment you’re in.'
              : 'Create your account while it builds. You’ll see it the moment it’s ready.',
            ...(linkEmail ? { email: linkEmail } : {}),
          }}
        />
      )}
      {isAuthenticated && status !== 'running' && !heldApp && !nothing && (
        <p className="type-caption">Bringing your app back…</p>
      )}
    </Screen>
  );
}

/** About how long a build takes end to end — what this screen has always told people. */
export const EXPECTED_BUILD_MS = 4 * 60_000;

/*
 * The build's real stages (src/webapp/generateWebApp.ts: write, check, repair, assemble), at the
 * times they usually begin. The server answers once, at the end, so these follow the clock; the
 * last holds until the app arrives.
 */
const STAGES = [
  { at: 0, label: 'Reading your plan' },
  { at: 15_000, label: 'Writing the pages' },
  { at: 150_000, label: 'Checking every button works' },
  { at: 210_000, label: 'Putting it together' },
];

/** When this build began: its key's age, which a sign-in in the middle does not reset. */
function buildStartedAt(): number {
  const at = Date.parse(readIntent()?.startedAt ?? '');
  return Number.isFinite(at) ? at : Date.now();
}

/**
 * Progress people can see (2026-10-09): a visitor left the build screen 18 seconds in, and another
 * 7 seconds after signing in, in front of a sliver of a bar that never moved and a line that never
 * changed for four minutes. A clock, a bar that fills, and the stage it is at.
 */
/** The build's clock: time so far, the bar's fill and the stage it has reached, ticking each second. */
function useBuildClock(startedAt: number): { pct: number; stage: number; clock: string } {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tick);
  }, []);
  const elapsed = Math.max(0, now - startedAt);
  // Never full before it is done: the last stretch waits on the app itself.
  const pct = Math.min(95, Math.round((elapsed / EXPECTED_BUILD_MS) * 100));
  const stage = STAGES.filter((s) => elapsed >= s.at).length - 1;
  const seconds = Math.floor(elapsed / 1000);
  return { pct, stage, clock: `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}` };
}

function Meter({ pct }: { pct: number }) {
  return (
    <div className="building-meter" role="progressbar" aria-label="Building your app" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
      <span style={{ width: `${pct}%` }} />
    </div>
  );
}

function BuildProgress({ startedAt }: { startedAt: number }) {
  const { pct, stage, clock } = useBuildClock(startedAt);
  return (
    <div className="building-progress">
      <Meter pct={pct} />
      <p className="type-caption building-elapsed">{clock} so far · usually about 4 minutes</p>
      <ul className="plan-progress building-stages">
        {STAGES.map((s, i) => (
          <li key={s.label} className={i < stage ? 'plan-progress-done' : i === stage ? 'plan-progress-now' : 'building-stage-later'}>
            {s.label}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * V7 (V7-A/04-building-email): signed out, the build is one compact block at the top — "Building
 * your … · 0:20", the bar, and the stage as one line — so the email card is the screen.
 */
function BuildHeader({ startedAt, title }: { startedAt: number; title: string }) {
  const { pct, stage, clock } = useBuildClock(startedAt);
  return (
    <div className="building-head">
      <h1 className="type-heading building-head-title">
        <span className="building-head-spinner" aria-hidden="true" />
        <span>
          {title}
          <span className="building-head-clock"> · {clock}</span>
        </span>
      </h1>
      <Meter pct={pct} />
      <p className="type-caption building-head-stage" role="status" aria-live="polite">
        {/* No "step 1 of 4" (owner, 2026-10-20): the stage's name, not how many are left. */}
        {STAGES[stage].label}
      </p>
    </div>
  );
}

/** V7-A/05-sent: the stages, under "Still building", while they are told they may close the page. */
function StageList({ startedAt }: { startedAt: number }) {
  const { stage } = useBuildClock(startedAt);
  return (
    <section className="building-still" aria-label="Still building">
      <h2 className="type-micro building-still-label">Still building</h2>
      <ul className="plan-progress building-stages">
        {STAGES.map((s, i) => (
          <li key={s.label} className={i < stage ? 'plan-progress-done' : i === stage ? 'plan-progress-now' : 'building-stage-later'}>
            {s.label}
          </li>
        ))}
      </ul>
    </section>
  );
}

const READY_SENT_KEY = 'saddle.readySent';

/** The address the "it's ready" email was asked for, for this build: a reload still says it was sent. */
function readySentFor(requestKey: string | undefined): string | null {
  if (!requestKey) return null;
  try {
    const kept = JSON.parse(sessionStorage.getItem(READY_SENT_KEY) ?? 'null') as { requestKey?: string; email?: string } | null;
    return kept?.requestKey === requestKey && kept.email ? kept.email : null;
  } catch {
    return null;
  }
}
function rememberReadySent(requestKey: string, email: string): void {
  try {
    sessionStorage.setItem(READY_SENT_KEY, JSON.stringify({ requestKey, email }));
  } catch {
    /* said on this page; a reload asks again */
  }
}

/** What the app is called in a sentence, from "What is it?" when it was asked (V7): "your tracker". */
function thingBuilt(): string {
  const what = useWhat.getState();
  return what.prompt.trim() ? TYPES[what.type].thing : 'app';
}

/**
 * The ready link asked for this tab's build: "Email me when it's ready", or the address given before the
 * build (`auto`). Never throws; a refusal comes back with its code and the words for the screen.
 */
async function sendReadyLink(
  address: string,
  appName: string,
  auto = false,
): Promise<{ ok: true } | { ok: false; code: string; text: string }> {
  const intent = readIntent();
  if (!intent?.requestKey) return { ok: false, code: 'no_build', text: 'There’s no app building in this browser to send.' };
  try {
    const what = useWhat.getState();
    const answer = await requestReadyLink({
      requestKey: intent.requestKey,
      email: address,
      goal: intent.goal,
      appName: intent.appName || appName,
      // "Open my golf tracker" on the email's button.
      thing: emailNoun(intent.goal, { demo: what.demo, type: what.type }),
      plan: intent.plan ?? useDraft.getState().plan,
    });
    funnelStep('ready_link_requested', { code: 'sent' in answer ? 'sent' : 'queued', ...(auto ? { auto: true } : {}) });
    rememberReadySent(intent.requestKey, address);
    return { ok: true };
  } catch (err) {
    const refusal = readyLinkRefusal(err);
    funnelStep('error_shown', { where: auto ? 'early_email' : 'ready_link', code: refusal.code });
    return { ok: false, code: refusal.code, text: refusal.text };
  }
}

/**
 * "Get it by email" (V7-A/04-building-email): the email first, because most people will not sit
 * through four minutes. The build's finisher emails a link the moment it is done; the link confirms
 * the address and signs them in (screens/new/Ready.tsx). Apple beside it, and "I'll wait here" for
 * today's sign-up card.
 */
function ReadyByEmail({ appName, onSent, onWait }: { appName: string; onSent: (email: string) => void; onWait: () => void }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  // Not inside an app's own browser, where Apple's sign-in spread over tabs and lost the build (SignUp.tsx).
  const showApple = socialProviders().includes('apple') && typeof navigator !== 'undefined' && inAppBrowser(navigator.userAgent) === 'none';
  const thing = thingBuilt();

  async function send(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (sending) return;
    const address = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) {
      setProblem('Enter your full email, like you@example.com');
      return;
    }
    setSending(true);
    setProblem(null);
    const result = await sendReadyLink(address, appName);
    setSending(false);
    if (result.ok) onSent(address);
    else setProblem(result.text);
  }

  return (
    <section className="card clay-card ready-card" aria-label="Get it by email">
      <h2 className="type-title ready-title">Get it by email</h2>
      <p className="type-body ready-sub">No need to wait here. We’ll send it the moment it’s built.</p>
      <form onSubmit={(e) => void send(e)} noValidate>
        <TextField
          label="Email"
          type="email"
          value={email}
          onChangeText={setEmail}
          autoComplete="email"
          inputMode="email"
          placeholder="you@example.com"
          error={problem ?? undefined}
        />
        <Button title="Email me when it’s ready" loading={sending} type="submit" />
        <p className="type-caption ready-note">Tap the link in the email to confirm it’s you. Your {thing} opens straight away.</p>
      </form>
      {showApple && (
        <>
          <p className="social-or type-caption" aria-hidden="true"><span>or</span></p>
          <SignUp card={{ from: '/new/building', only: 'apple' }} />
        </>
      )}
      <p className="ready-wait">
        <button type="button" className="link-button ready-wait-button" onClick={onWait}>I’ll wait here</button>
      </p>
    </section>
  );
}

/** V7-A/05-sent: said once, with the address, and that the page can be closed. */
function ReadySent({ email, built }: { email: string; built: boolean }) {
  return (
    <section className="card clay-card ready-sent" role="status" aria-label="Email on its way">
      <span className="ready-sent-icon" aria-hidden="true">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
      </span>
      <p className="type-title ready-sent-line">
        {built ? `We’ve emailed ${email} your link.` : `We’ll email ${email} when it’s ready (about 4 minutes).`}
      </p>
      <p className="type-body ready-sent-sub">You can close this page.</p>
    </section>
  );
}

/** Their site, then the offer over it. */
/** `preview`: the URL the site serves the held app at, or null when there is nothing to show. */
function SiteThenOffer({ appName, preview, plan, ownerId }: { appName: string; preview: string | null; plan: AppPlan | null; ownerId: string }) {
  const navigate = useNavigate();
  const [offerOpen, setOfferOpen] = useState(false);
  const [offerShown, setOfferShown] = useState(false);
  const [offer, setOffer] = useState<Offer>('trial');
  /** A running free week's end, when the trial charges then rather than in 14 days. */
  const [trialEndsAt, setTrialEndsAt] = useState<string | undefined>(undefined);
  const [giftEnded, setGiftEnded] = useState<'used_up' | 'expired' | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  /** What the trip to Stripe cannot carry, once said: the next press goes on (as on the plan). */
  const [notKeptSaid, setNotKeptSaid] = useState<StashResult | null>(null);

  // Back from Stripe by the browser's Back (the page restored as it was left): the button is given back.
  useEffect(() => {
    const shown = (e: PageTransitionEvent): void => {
      if (e.persisted) setBusy(false);
    };
    window.addEventListener('pageshow', shown);
    return () => window.removeEventListener('pageshow', shown);
  }, []);
  useFunnelStep('site_preview_viewed', preview !== null);
  useFunnelStep('paywall_viewed', offerOpen, { offer });

  useEffect(() => {
    void creditBalance()
      .then((b) => {
        setOffer(offerFor(b));
        setTrialEndsAt(freeWeekEnd(b));
        setGiftEnded(b.gift?.ended ?? null);
      })
      .catch(() => undefined);
    // With a site to look at, a moment first; without one, the offer at once.
    const timer = setTimeout(() => {
      setOfferOpen(true);
      setOfferShown(true);
    }, preview === null ? 0 : PREVIEW_BEFORE_OFFER_MS);
    return () => clearTimeout(timer);
  }, []);

  /*
   * paywall_left (as on the plan): the card put away, or the page closed with it open — never the
   * trip to Stripe, which is checkout_started.
   */
  const openedAt = useRef<number | null>(null);
  const toCheckout = useRef(false);
  useEffect(() => {
    if (offerOpen) openedAt.current = Date.now();
  }, [offerOpen]);
  function noteLeft(now: boolean): void {
    if (openedAt.current === null || toCheckout.current) return;
    funnelStep('paywall_left', { offer, seconds: secondsBucket(Date.now() - openedAt.current) }, now ? { now: true } : undefined);
    openedAt.current = null;
  }
  useEffect(() => {
    const onHide = (): void => noteLeft(true);
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  });

  const [askingExit, setAskingExit] = useState(false);
  /** The offer put away: over their site, a bar stays and the site stays dimmed; with no site, back to the plan. */
  function putAway(): void {
    if (preview !== null) setOfferOpen(false);
    else navigate('/new/plan');
  }

  async function checkout(plan: PlanId): Promise<void> {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    const kept = await stashDraft(ownerId);
    if ((!kept.saved || kept.lost.length > 0) && !notKeptSaid) {
      setNotKeptSaid(kept);
      setBusy(false);
      return;
    }
    rememberCheckoutOffer(offer, offer === 'trial' ? { plan, chargeAt: trialEndsAt ?? trialEndFromNow().toISOString() } : { plan });
    const result = await startCheckout(
      'subscription',
      (url) => {
        toCheckout.current = true;
        funnelStep('checkout_started', { offer }, { now: true });
        if (reportInitiateCheckout(ownerId, useDraft.getState().description)) setTimeout(() => leaveFor(url), 300);
        else leaveFor(url);
      },
      { resume: 'build', plan },
    );
    if (result.status === 'redirecting') return;
    setBusy(false);
    setNotice(result.status === 'signin_required' ? 'Your session has ended. Sign in again to start your trial.' : result.message);
  }

  const shownPlan: AppPlan = plan ?? ({ appName, features: [] } as unknown as AppPlan);
  return (
    <div className={offerShown ? 'site-preview site-preview-dimmed' : 'site-preview'}>
      <div className="site-preview-bar type-caption">
        <span>{preview !== null ? `Your ${appName} · preview` : `Your ${appName}`}</span>
      </div>
      {preview !== null ? (
        /*
          Served by the site, not drawn here (api/build.ts, heldPreviewUrl): another origin, so it
          runs with its own storage and can never read the builder's.
        */
        <iframe
          className="site-preview-frame"
          title={`${appName}, a preview`}
          src={preview}
          referrerPolicy="no-referrer"
          sandbox="allow-scripts allow-forms allow-same-origin"
        />
      ) : (
        <div className="site-preview-frame" aria-hidden="true" />
      )}
      {offerShown && (
        <div className={offerOpen ? 'site-offer site-offer-open' : 'site-offer'} aria-live="polite">
          {askingExit ? (
            <div className="paywall"><ExitQuestion onDone={() => { setAskingExit(false); putAway(); }} /></div>
          ) : offerOpen ? (
            <Paywall
              plan={{ ...shownPlan, appName }}
              offer={offer}
              trialEndsAt={trialEndsAt}
              ready={preview !== null}
              giftEnded={giftEnded}
              busy={busy}
              continueLabel={notKeptSaid ? 'Continue to checkout' : undefined}
              notice={
                <>
                  {notKeptSaid && (
                    <p role="status" className="type-caption plan-detail plan-notice">
                      {notKeptSaid.saved
                        ? notKept(notKeptSaid.lost)
                        : "This browser couldn't keep your plan while you're at checkout, so you'd need to describe it again afterwards."}
                    </p>
                  )}
                  {notice && <p role="alert" data-error-code="checkout" className="type-caption field-error-text">{notice}</p>}
                </>
              }
              onContinue={(plan) => void checkout(plan)}
              backLabel={preview !== null ? 'Not now' : 'Back to your plan'}
              onBack={() => {
                noteLeft(false);
                // Asked once, ever, what stopped them (ExitQuestion) — then put away as before.
                if (exitQuestionAsked()) putAway();
                else setAskingExit(true);
              }}
            />
          ) : (
            <div className="site-offer-bar">
              <span className="type-body">Your {appName} is ready to go live.</span>
              <Button title={offer === 'trial' ? 'Start your free trial' : 'Choose a plan'} onPress={() => setOfferOpen(true)} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
